import { create } from 'zustand';
import { defineSyncedStore, type SyncedStoreDef } from './yjs-provider';
import type { AnalysisStatus } from '~types/content-analysis';

/**
 * Replication declaration (aggregated by src/store/registry.ts).
 * merge-defaults + scopedDiff: regenerable AI cache, lowest data criticality —
 * worst case is re-running analysis.
 */
export const CONTENT_ANALYSIS_STORE_DEF: SyncedStoreDef<'sections'> = {
    name: 'contentAnalysis',
    syncedKeys: ['sections'],
    hydration: 'merge-defaults',
    scopedDiff: true,
};

/**
 * A table adaptation entry (CFI -> spoken text).
 */
export interface TableAdaptation {
    rootCfi: string;
    text: string;
}

/**
 * Analysis data for a single section.
 */
export interface SectionAnalysis {
    /** The rootCfi marking the beginning of the references section, if any. */
    referenceStartCfi?: string;
    /** AI-generated spoken text for tables. */
    tableAdaptations?: TableAdaptation[];
    /** Section title extracted via AI. */
    title?: string;
    /** Generation timestamp. */
    generatedAt: number;
    /** Current analysis status. */
    status?: AnalysisStatus;
    /** Last error message if status is 'error'. */
    lastError?: string;
    /** Timestamp of the last attempt. */
    lastAttempt?: number;
}

/**
 * State interface for the Content Analysis store (Synced).
 * 
 * This store handles AI-generated content analysis that is expensive to regenerate.
 * It is wrapped with yjs() middleware to sync across devices.
 * 
 * Key structure: `${bookId}/${sectionId}` -> SectionAnalysis
 */
interface ContentAnalysisState {
    // === SYNCED STATE (persisted to Yjs) ===
    /** 
     * Map of section analysis data.
     * Key: `${bookId}/${sectionId}` (using / to allow easy parsing)
     */
    sections: Record<string, SectionAnalysis>;

    // === ACTIONS ===
    /**
     * Saves the start CFI for references in a section.
     */
    saveReferenceStartCfi: (
        bookId: string,
        sectionId: string,
        referenceStartCfi?: string
    ) => void;

    /**
     * Saves table adaptations for a section.
     */
    saveTableAdaptations: (
        bookId: string,
        sectionId: string,
        adaptations: TableAdaptation[]
    ) => void;

    /**
     * Saves section title.
     */
    saveSectionTitle: (
        bookId: string,
        sectionId: string,
        title: string
    ) => void;

    /**
     * Marks analysis as loading.
     */
    markAnalysisLoading: (bookId: string, sectionId: string) => void;

    /**
     * Marks analysis as failed with an error.
     */
    markAnalysisError: (bookId: string, sectionId: string, error: string) => void;

    /**
     * Gets analysis data for a section, if available.
     */
    getAnalysis: (bookId: string, sectionId: string) => SectionAnalysis | undefined;

    /**
     * Deletes all analysis data for a book.
     */
    deleteBookAnalysis: (bookId: string) => void;

    /**
     * Clears all analysis data.
     */
    clearAll: () => void;
}

/**
 * Generates the composite key for a section.
 */
function makeKey(bookId: string, sectionId: string): string {
    return `${bookId}/${sectionId}`;
}

export const useContentAnalysisStore = create<ContentAnalysisState>()(
    defineSyncedStore(
        CONTENT_ANALYSIS_STORE_DEF,
        (set, get) => ({
            sections: {},

            saveReferenceStartCfi: (bookId, sectionId, referenceStartCfi) =>
                set((state) => {
                    const key = makeKey(bookId, sectionId);
                    const existing = state.sections[key] || { generatedAt: Date.now() };
                    return {
                        sections: {
                            ...state.sections,
                            [key]: {
                                ...existing,
                                referenceStartCfi,
                                generatedAt: Date.now(),
                                status: 'success',
                                lastAttempt: Date.now(),
                                lastError: undefined
                            }
                        }
                    };
                }),

            saveTableAdaptations: (bookId, sectionId, adaptations) =>
                set((state) => {
                    const key = makeKey(bookId, sectionId);
                    const existing = state.sections[key] || { generatedAt: Date.now() };

                    // Merge with existing adaptations (de-duplicating by rootCfi)
                    const existingAdaptations = existing.tableAdaptations || [];
                    const existingMap = new Map(existingAdaptations.map(a => [a.rootCfi, a]));

                    // New adaptations overwrite existing ones for same CFI
                    for (const adaptation of adaptations) {
                        existingMap.set(adaptation.rootCfi, adaptation);
                    }

                    return {
                        sections: {
                            ...state.sections,
                            [key]: {
                                ...existing,
                                tableAdaptations: Array.from(existingMap.values()),
                                generatedAt: Date.now(),
                                status: 'success',
                                lastAttempt: Date.now(),
                                lastError: undefined
                            }
                        }
                    };
                }),

            saveSectionTitle: (bookId, sectionId, title) =>
                set((state) => {
                    const key = makeKey(bookId, sectionId);
                    const existing = state.sections[key] || { generatedAt: Date.now() };
                    return {
                        sections: {
                            ...state.sections,
                            [key]: {
                                ...existing,
                                title,
                                generatedAt: Date.now()
                            }
                        }
                    };
                }),

            markAnalysisLoading: (bookId, sectionId) =>
                set((state) => {
                    const key = makeKey(bookId, sectionId);
                    const existing = state.sections[key] || { generatedAt: Date.now() };
                    return {
                        sections: {
                            ...state.sections,
                            [key]: {
                                ...existing,
                                status: 'loading',
                                lastAttempt: Date.now()
                            }
                        }
                    };
                }),

            markAnalysisError: (bookId, sectionId, error) =>
                set((state) => {
                    const key = makeKey(bookId, sectionId);
                    const existing = state.sections[key] || { generatedAt: Date.now() };
                    return {
                        sections: {
                            ...state.sections,
                            [key]: {
                                ...existing,
                                status: 'error',
                                lastError: error,
                                lastAttempt: Date.now()
                            }
                        }
                    };
                }),

            getAnalysis: (bookId, sectionId) => {
                const key = makeKey(bookId, sectionId);
                return get().sections[key];
            },

            deleteBookAnalysis: (bookId) =>
                set((state) => {
                    const prefix = `${bookId}/`;
                    const remaining: Record<string, SectionAnalysis> = {};

                    for (const [key, value] of Object.entries(state.sections)) {
                        if (!key.startsWith(prefix)) {
                            remaining[key] = value;
                        }
                    }

                    return { sections: remaining };
                }),

            clearAll: () => set({ sections: {} })
        })
    )
);
